import { useEffect, useRef, useState } from 'react'
// import videojs from 'video.js'
// import Player from 'video.js/dist/types/player'
import { getVideoCodec } from '../utils'
// type VideoJsPlayer = Player

export type VideoType = {
  // object: HTMLVideoElement | VideoJsPlayer
  object: any
  element: HTMLVideoElement
  src: (src: string) => void
  play: () => void
  pause: () => void
  paused: () => boolean
  muted: (muted: boolean) => void
  volume: (vol?: number) => void | number
  loop: (loop: boolean) => void
  autoplay: (autoplay: boolean) => void
  duration: () => number | undefined
  dispose: () => void
  currentTime: (time?: number) => void | number
  playsinline: (playsinline: boolean) => void
  on: (event: string, callback: () => void) => void
  off: (event: string, callback: () => void) => void
}

export const useCreateVideo = (): VideoType | null => {
  const [video, setVideo] = useState<VideoType | null>(null)
  const isFirst = useRef(true)

  useEffect(() => {
    if (!isFirst.current) {
      return
    }
    isFirst.current = false
    createVideo()
  }, [])

  const makeVideo = () => {
    const videoJSElement: HTMLElement = document.createElement('video-js')
    const options: any = {
      loop: true,
      controls: true,
      muted: true,
      playsinline: true,
      autoplay: true,
      preload: 'metadata',
      crossOrigin: 'use-credentials',
      html5: {
        vhs: { withCredentials: true },
        dash: { setXHRWithCredentialsForType: ['MPD', true] },
        nativeTextTracks: false,
      },
    }

    // return videojs(videoJSElement, options) as VideoJsPlayer
    // @ts-ignore
    return window.videojs(videoJSElement, options)
  }

  const createVideo = () => {
    const player = makeVideo()

    const setCurrentTime = (time: number) => {
      player.currentTime(time)
    }

    const getCurrentTime = (): number => {
      return player.currentTime() as number
    }

    const setVolume = (vol: number) => {
      player.volume(vol)
    }

    const getVolume = (): number => {
      return player.volume() as number
    }

    const videoObject = {
      object: player,
      element: player instanceof HTMLVideoElement ? player : (player as any).tech_.el_,
      src: (src: string): void => {
        if (!player) {
          return
        }

        player.crossOrigin('use-credentials')
        const codecType = getVideoCodec()
        const srcSettings = [
          {
            src,
            type: codecType === 'H265_DASH'
              ? 'application/dash+xml'
              : (codecType === 'H265_HLS' ? 'application/x-mpegURL' : 'video/webm; codecs="vp9"'),
            /*
            type: codecType === 'H265_DASH'
              ? 'application/dash+xml'
              : (codecType === 'H265_HLS' ? 'application/x-mpegURL' : 'application/dash+xml; codecs="vp9"'),
             */
            withCredentials: true,
          },
        ]
        // console.log('settings: ', srcSettings)
        player.src(srcSettings)

        // The mediaPlayer settings can only be configured after setting the src, so configure them here
        const onReadyFunc = () => {
          const videoJsPlayer = player as any
          if (videoJsPlayer.dash?.mediaPlayer) {
            videoJsPlayer.dash.mediaPlayer.setXHRWithCredentialsForType('GET', true)
            videoJsPlayer.dash.mediaPlayer.setXHRWithCredentialsForType('MPD', true)
            videoJsPlayer.dash.mediaPlayer.setXHRWithCredentialsForType('MediaSegment', true)
            videoJsPlayer.dash.mediaPlayer.setXHRWithCredentialsForType('InitializationSegment', true)
            videoJsPlayer.dash.mediaPlayer.setXHRWithCredentialsForType('IndexSegment', true)
            videoJsPlayer.dash.mediaPlayer.setXHRWithCredentialsForType('other', true)
          }
          videoJsPlayer.off('ready', onReadyFunc)
        }
        player.ready(onReadyFunc)
      },
      play: (): void => {
        if (!player) return
        player.play()
      },
      pause: (): void => {
        if (!player) return
        player.pause()
      },
      paused: (): boolean => {
        return player.paused()
      },
      currentTime: (time?: number): void | number => {
        if (!player) {
          return
        }

        if (time) {
          setCurrentTime(time)
        } else {
          return getCurrentTime()
        }
      },
      muted: (muted: boolean): void => {
        if (!player) return

        player.muted(muted)
      },
      volume: (vol?: number): void | number => {
        if (!player) {
          return
        }

        if (vol) {
          setVolume(vol)
        } else {
          return getVolume()
        }
      },
      loop: (loop: boolean): void => {
        if (!player) return

        player.loop(loop)
      },
      autoplay: (autoplay: boolean): void => {
        if (!player) return

        player.autoplay(autoplay)
      },
      duration: (): number | undefined => {
        return player.duration()
      },
      playsinline: (playsinline: boolean): void => {
        if (!player) {
          return
        }

        player.playsinline(playsinline)
      },
      dispose: (): void => {
        if ('dispose' in player) {
          player.dispose()
        }
      },
      on: (event: string, callback: () => void): void => {
        if (!player) {
          return
        }

        player.on(event, callback)
      },
      off: (event: string, callback: () => void): void => {
        if (!player) {
          return
        }

        player.off(event, callback)
      },
    }

    setVideo(videoObject)
  }

  return video
}
